import { normalizeRegistrationNumber } from '@eidas-pro/shared';
import { z } from 'zod';

// Email validation (strict RFC 5322 subset)
export const emailSchema = z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(254, 'Email is too long')
    .toLowerCase()
    .trim();

// Name validation
export const nameSchema = z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long')
    .regex(/^[a-zA-Z\s'\-.]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes')
    .trim();

// Company name (optional)
export const companySchema = z.string().max(200, 'Company name is too long').trim().optional().or(z.literal(''));

// Country (optional)
export const countrySchema = z.string().max(100, 'Country name is too long').trim().optional().or(z.literal(''));

// Subject line
export const subjectSchema = z
    .string()
    .min(1, 'Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(200, 'Subject is too long')
    .trim();

// Message/textarea
export const messageSchema = z
    .string()
    .min(1, 'Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message is too long')
    .trim();

// Search query
export const searchQuerySchema = z.string().max(200, 'Search query is too long').trim();

// Consent checkbox
export const consentSchema = z.boolean().refine((val) => val === true, 'You must accept the privacy policy');

// Captcha token (can be null initially)
export const captchaSchema = z.string().min(1, 'Please complete the captcha').nullable();

// Form-specific schemas (composed from above)
export const newsletterFormSchema = z.object({
    email: emailSchema,
    gdprConsent: consentSchema,
    captchaToken: captchaSchema,
});

export const waitingListFormSchema = z.object({
    email: emailSchema,
    company: companySchema,
    country: countrySchema,
    consent: consentSchema,
    captchaToken: captchaSchema,
});

export const supportFormSchema = z.object({
    name: nameSchema,
    email: emailSchema,
    subject: subjectSchema,
    message: messageSchema,
    captchaToken: captchaSchema,
});

export const unsubscribeFormSchema = z.object({
    email: emailSchema,
    captchaToken: captchaSchema,
});

// EU member states: the programme pays businesses under the EU reverse-charge rule. Shared by the form and the API.
export const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
    'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
] as const; // prettier-ignore

export const PARTNER_TYPES = ['AGENCY', 'CONSULTANT', 'ASSOCIATION'] as const;

const optionalBlank = (schema: z.ZodType<string>) =>
    z.preprocess((value) => (typeof value === 'string' && value.trim() === '' ? null : value), schema.nullish());

/**
 * Partner application (affiliate spec §5, businesses only). The limits mirror what
 * `PartnerService.submitApplication` accepts, so the route answers 400 with the failing fields instead
 * of accepting an application the service would then refuse in `after()`. The CAPTCHA token is
 * checked separately by the route.
 */
export const partnerApplicationSchema = z.object({
    type: z.enum(PARTNER_TYPES),
    legalName: z.string().trim().min(2).max(255),
    registrationNumber: z
        .string()
        .max(64)
        .refine((value) => normalizeRegistrationNumber(value) !== null, 'Enter a KvK, VAT or RCS number'),
    country: z.enum(EU_COUNTRIES, { message: 'Choose an EU member state' }),
    contactName: z.string().trim().min(2).max(255),
    contactEmail: z.string().trim().max(255).email('Please enter a valid email address'),
    website: optionalBlank(
        z
            .string()
            .trim()
            .max(255)
            .refine((value) => /^https?:\/\//i.test(value) && URL.canParse(value), 'Enter an http(s) address')
    ),
    message: optionalBlank(z.string().trim().max(2000)),
    consent: z.literal(true),
});

// Export types
export type PartnerApplicationData = z.infer<typeof partnerApplicationSchema>;
export type NewsletterFormData = z.infer<typeof newsletterFormSchema>;
export type WaitingListFormData = z.infer<typeof waitingListFormSchema>;
export type SupportFormData = z.infer<typeof supportFormSchema>;
export type UnsubscribeFormData = z.infer<typeof unsubscribeFormSchema>;
